import React from "react";
import FadeReveal from "./FadeReveal";

const educationAndWork = [
  {
    date: "2023 – Present",
    title: "Computer Science Student",
    org: "Politehnica University of Timișoara",
    description:
      "Third-year student focusing on software development, algorithms, and web technologies. Relevant coursework includes Web Development, Software Engineering, and Database Systems.",
  },
  {
    date: "Jul 2026 – Aug 2026",
    title: "Summer Trainee",
    org: "Nokia Networks Romania",
    description:
      "Completed a summer trainee program gaining hands-on experience in telecommunications. Part of the Plane Integration Team — integrated and tested network components, worked with SSH access, virtual machines, simulated environments, and network protocols.",
  },
  {
    date: "Apr 2026 – Jun 2026",
    title: "MHP Stack Unlocked Laboratory",
    org: "MHP – A Porsche Company",
    description:
      "10-week hands-on workshops across Cybersecurity, AWS, AI, and Project Management, solving real-world team challenges:",
    list: [
      ["Cybersecurity:", " IP/firewall configuration, pentesting, and vulnerability assessment"],
      ["AI:", " Built a local RAG pipeline — trained a model on a PDF for natural-language Q&A"],
      ["AWS:", " Developed a cloud file-storage app (Google Drive-style) using Infrastructure as Code"],
      ["Project Management:", " Agile/Scrum fundamentals and sprint planning"],
    ],
  },
  {
    date: "2025",
    title: "Cybersecurity Accredited Course",
    org: "ISC² via Thor Pedersen",
    description:
      "Completed The Complete Certified in Cybersecurity (CC) course, obtaining foundational skills in maintaining secure systems.",
  },
];

const achievements = [
  {
    date: "2026 – Present",
    title: "FloorWars — Campus Territory Game",
    org: "Android team project · in development",
    description:
      "A team-built Android game in development where students claim campus buildings for their faculty by taking the stairs — no hardware in the buildings:",
    list: [
      ["Detection:", " Kotlin foreground service fuses barometer and step-detector data to tell stair climbs from elevator rides, flat walking, and shaking"],
      ["Check-in:", " Signed rotating QR codes at building entrances or geofencing; offline segment storage with idempotent sync"],
      ["Backend:", " FastAPI, PostgreSQL/PostGIS, Valkey, Celery; live standings over WebSocket and layered anti-cheat checks"],
      ["Stack:", " React Native (TypeScript), Docker Compose + Caddy on Azure, GitHub Actions CI"],
    ],
  },
  {
    date: "2026 – Present",
    title: "LSFMT — Student Association Platform",
    org: "Full-stack web platform · ongoing",
    description:
      "An ongoing full-stack platform for a student association, launching in the near future — a public-facing site paired with a secured admin CMS:",
    list: [
      ["Public site:", " Articles, programs, representatives, and FAQ sections"],
      ["Admin panel:", " Authenticated admins manage articles via a rich-text editor, plus programs and representatives"],
      ["Media:", " Image and PDF uploads with cover-image repositioning"],
      ["Stack:", " React, TypeScript, Supabase with row-level security, Vite — live at lsfmt.vercel.app"],
    ],
  },
  {
    date: "Apr 2026 – Jun 2026",
    title: "Water Quality Monitoring System",
    org: "Politehnica University of Timișoara",
    description:
      "End-to-end IoT platform: physical sensors on a Raspberry Pi stream real-time water readings to AWS, where an LSTM Autoencoder detects anomalies and a two-layer alert engine sends email/SMS via SNS:",
    list: [
      ["Edge:", " Raspberry Pi reads pH, TDS, turbidity & temperature every 10s and publishes to AWS IoT Core over MQTT/TLS"],
      ["Cloud:", " IoT Core → Lambda → Django REST API → PostgreSQL pipeline; alerts via Amazon SNS"],
      ["ML:", " LSTM Autoencoder trained on 20K readings (20 engineered features), exported to ONNX for server-side inference"],
      ["Alerting:", " Two-layer decision matrix — ML confidence gates all notifications to suppress noise"],
    ],
  },
  {
    date: "2026",
    title: "iTEC Timișoara — Hackathon",
    org: "iTECify · 48h sprint",
    description:
      "Team project built in a 48h sprint — a collaborative coding platform with real-time editing, secure code execution, and AI assistance:",
    list: [
      ["Frontend:", " React 18, Vite, Tailwind CSS, Monaco Editor, Zustand"],
      ["Backend:", " Fastify, WebSocket streaming, Zod validation"],
      ["Infra:", " Docker sandboxes for secure code execution with resource constraints"],
      ["Auth & AI:", " Supabase authentication and Gemini AI coding assistant"],
    ],
  },
  {
    date: "2025",
    title: "UniHack Timișoara — Hackathon",
    org: "Echoes · 72h sprint",
    description:
      "Team project built in a 72h sprint — a mobile-first PWA that turns Timișoara into an interactive GPS-powered cultural heritage trail:",
    list: [
      ["Location:", " Proximity-based unlocking of audio stories within 50m of 8+ landmarks"],
      ["Map:", " React + Leaflet interactive map with real-time user tracking"],
      ["AI:", " Gemini API for dynamic narrations and translations in 5 languages, plus a tourist chatbot"],
      ["Gamification:", " Supabase-powered points, leaderboards, custom routes, and social sharing"],
    ],
  },
];

const TimelineItem = ({ item }) => (
  <div className="xp-item">
    <span className="xp-date">{item.date}</span>
    <h3>{item.title}</h3>
    <div className="xp-org">{item.org}</div>
    <p>{item.description}</p>
    {item.list && (
      <ul className="xp-list">
        {item.list.map(([strong, rest]) => (
          <li key={strong}>
            <strong>{strong}</strong>
            {rest}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const Experience = () => {
  return (
    <section id="experience">
      <div className="shell">
        <div className="section-head">
          <FadeReveal direction="up">
            <span className="eyebrow">Career story</span>
            <h2>Experience &amp; education</h2>
            <p className="lead">
              A record of where I've studied, worked, and shipped — from university
              coursework to industry programs and competitive hackathons.
            </p>
          </FadeReveal>
        </div>

        <div className="xp-layout">
          <div>
            <FadeReveal direction="up" delay={0.05}>
              <p className="xp-col-title">
                <span className="idx"></span> Education &amp; work
              </p>
            </FadeReveal>
            <FadeReveal direction="up" delay={0.1}>
              <div className="xp-track">
                {educationAndWork.map((item) => (
                  <TimelineItem key={item.title} item={item} />
                ))}
              </div>
            </FadeReveal>
          </div>

          <div>
            <FadeReveal direction="up" delay={0.05}>
              <p className="xp-col-title">
                <span className="idx"></span> Projects &amp; hackathons
              </p>
            </FadeReveal>
            <FadeReveal direction="up" delay={0.1}>
              <div className="xp-track">
                {achievements.map((item) => (
                  <TimelineItem key={item.title} item={item} />
                ))}
              </div>
            </FadeReveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
